import memoize from 'memoizee';
import BN from 'bignumber.js'
import { uniq, flattenArray } from '@/utils/Array';
import { sleepUntil } from '@/utils/Async';
import { multiCall } from '../Calls';
import getPricesForAssets from './getPricesForAssets';
import getPoolVirtualPrice from './getPoolVirtualPrice';
import pools from '../../constants/pools';
import { arrayToHashmap } from '@/utils/helpers';
import coins from '../../constants/coins';
import { ERC20_abi, multicall_abi, multicall_address, isFactoryAbiDataLoaded } from '../../constants/abis';
import Web3 from 'web3';
import config from '@/constants/config';

const web3 = new Web3(config.rpcUrl);

const poolsReferenceAssetsCoingeckoIds = uniq([
  ...pools.map(({ coingeckoInfo: { referenceAssetId } }) => referenceAssetId),
  ...Array.from(Object.values(coins)).map(({ coingeckoId }) => coingeckoId).filter((o) => o),
]);

const getRefAssetPrice = memoize(async (refAsset) => {
  const prices = await getPricesForAssets(poolsReferenceAssetsCoingeckoIds);
  const referenceAssetCoingeckoId = (
    refAsset === 'eth' ? 'ethereum' :
      refAsset === 'btc' ? 'bitcoin' : (
        pools.find((pool) => pool.referenceAsset === refAsset)?.coingeckoInfo?.referenceAssetId ||
        Array.from(Object.values(coins)).find((coin) => coin.type === refAsset)?.coingeckoId
      )
  );

  if (!referenceAssetCoingeckoId) {
    console.error(`NO REFERENCE ASSET ID FOUND FOR REFASSET = "${refAsset}", HENCE USD FIGURES CAN’T BE COMPUTED CORRECTLY`)
  }

  return prices[referenceAssetCoingeckoId];
}, {
  promise: true,
  maxAge: 10 * 1000, // 10s
});

const getCryptoPoolTokenPrices = memoize(async () => {
  await sleepUntil(() => pools.isFactoryPoolDataLoaded() && isFactoryAbiDataLoaded());
  const cryptoPools = pools.filter(({ cryptoPool }) => cryptoPool);

  const callsConfig = flattenArray(cryptoPools.map(({
    id,
    addresses: { swap, lpToken },
    coins,
    coinsInPlaceReplacements,
  }) => [
      {
        contract: new web3.eth.Contract(ERC20_abi, lpToken),
        methodName: 'totalSupply',
        metaData: { type: 'totalSupply', poolId: id },
      },
      ...coins.map((coin, i) => {
        const actualCoin = coinsInPlaceReplacements[i] || coin;
        const { id: coinId, address, type, decimals } = actualCoin;

        if (coinId === config.nativeAssetId) {
          return {
            contract: new web3.eth.Contract(multicall_abi, multicall_address),
            methodName: 'getEthBalance', // Multicall helper method, must use compatible multicall impl
            params: [swap],
            metaData: { type: 'balance', referenceAsset: coin.type, poolId: id, decimals },
          };
        }

        return {
          contract: new web3.eth.Contract(ERC20_abi, address),
          methodName: 'balanceOf',
          params: [swap],
          metaData: { type: 'balance', referenceAsset: type, poolId: id, decimals },
        };
      }),
    ]));

  const cryptoPoolsData = await multiCall(callsConfig);

  const referenceAssetPrices = arrayToHashmap(await Promise.all(
    cryptoPoolsData
      .filter(({ metaData: { referenceAsset } }) => !!referenceAsset)
      .map(({ metaData: { referenceAsset } }) => new Promise(async (res) => {
        const price = await getRefAssetPrice(referenceAsset);
        res([referenceAsset, price]);
      }))
  ));

  const groupedData = cryptoPoolsData.reduce((accu, { data, metaData: { poolId, type, referenceAsset, decimals } }) => {
    const { totalSupply, totalBalancesUsdValue } = accu[poolId] || { totalSupply: 0, totalBalancesUsdValue: 0 };

    return {
      ...accu,
      [poolId]: {
        totalSupply: (
          type === 'totalSupply' ?
            (data / 1e18) :
            totalSupply
        ),
        totalBalancesUsdValue: (
          type === 'balance' ?
            totalBalancesUsdValue + (data / decimals * referenceAssetPrices[referenceAsset]) :
            totalBalancesUsdValue
        ),
      },
    };
  }, {});

  const lpTokenPrices = Array.from(Object.entries(groupedData)).reduce((accu, [poolId, { totalSupply, totalBalancesUsdValue }]) => ({
    ...accu,
    [poolId]: (totalBalancesUsdValue / totalSupply),
  }), {});

  return lpTokenPrices;
}, {
  promise: true,
  maxAge: 10 * 60 * 1000, // 10 min
});

const getCryptoPoolTokenPrice = async (pool) => {
  const data = await getCryptoPoolTokenPrices();
  return data[pool.id];
};

// Returns the usd price, given a pool balance and a pool's reference asset.
// `balance` can be either a Number or a BigNumber; the returned value will be
// either a Number or a BigNumber accordingly.
const getPoolUsdFigure = async (amount, pool) => {
  const isFactoryV2Pool = (
    typeof pool.totalSupply !== 'undefined' &&
    typeof pool.usdTotal !== 'undefined'
  );

  let virtualPrice;
  if (isFactoryV2Pool) {
    const poolAddress = pool.addresses?.swap || pool.address;
    if (poolAddress && pool.totalSupply > 0) {
      virtualPrice = await getPoolVirtualPrice(poolAddress);
    }
  }

  const refAssetPrice = (
    isFactoryV2Pool ? (pool.usdTotal / ((parseFloat(pool.totalSupply) === 0 ? 1 : pool.totalSupply) / 1e18 * (virtualPrice || 1))) :
      pool.cryptoPool ? await getCryptoPoolTokenPrice(pool) :
        await getRefAssetPrice(pool.referenceAsset)
  );

  return amount instanceof BN ?
    amount.times(refAssetPrice) :
    amount * refAssetPrice;
};

export default getPoolUsdFigure;
